<template>
  <div class="ways-menu">
    <section>
      <div class="label">
        <span>{{ $t("labels.maxLength") }}</span>
        <span class="label__annotation">
          {{ $t("texts.maxLengthExplanation") }}
        </span>
      </div>
      <input-number
        :min="1"
        :value="maxWayLength"
        @update:value="maxWayLength = $event"
      />
    </section>
    <section class="mt-4">
      <div class="label">{{ $t("objectNames.target", 1) }} 1</div>
      <item-combo-select
        v-show="wayStartItem == null"
        :targets="targets"
        :resources="{}"
        :indices="{}"
        :types="['target']"
        :net-choosable="false"
        @select-single="setStartItem"
      />
      <button-item
        v-if="wayStartItem"
        :icons="['x']"
        @click:x="wayStartItemId = null"
      >
        {{ wayStartItem.title }}
      </button-item>

      <div class="label mt-4">{{ $t("objectNames.target", 1) }} 2</div>
      <item-combo-select
        v-show="wayEndItem === null"
        :targets="targets"
        :resources="{}"
        :indices="{}"
        :types="['target']"
        :net-choosable="false"
        @select-single="setEndItem"
      />
      <!-- <input-dropdown
        v-show="wayEndItem === null"
        :options="targets"
        :order="targetsOrder"
        :clear-on-select="true"
        :searchable="true"
        prop="title"
        @select="setEndItem"
      /> -->
      <button-item
        v-if="wayEndItem !== null"
        :icons="['x']"
        @click:x="wayEndItemId = null"
      >
        {{ wayEndItem.title }}
      </button-item>
    </section>
    <section v-if="startAndEndSet" class="mt-4">
      <div v-if="allWaysBetweenSelectedItems.length > 0">
        <h3 class="ways-menu__waysheader label">
          <div>
            {{
              $t("headings.amountWaysFound", allWaysBetweenSelectedItems.length)
            }}
          </div>
          <div class="ta-r">
            {{ $t("labels.length") }}
          </div>
        </h3>
        <div class="ways-menu__wayslist">
          <radio-button
            v-for="way of limitedWaysBetweenItems"
            :key="way.id"
            :data="way.id"
            :value="selectedWayIndex"
            @update:value="selectedWayIndex = $event"
          >
            <div class="ways-menu__waycontent">
              <button-item
                :title="$t('actions.inspectWayInImpulseTracking')"
                @click="openAsWay($event, way)"
              >
                {{ $t("labels.wayX", [way.id + 1]) }}
              </button-item>
              <div class="button-item button-item_light relation-list-line__icon">
                <lucide-icon :name="way.inverse ? 'inverse' : 'not-inverse'" />
              </div>
              <span class="ta-r">{{ way.relationIds.length }}</span>
            </div>
          </radio-button>
        </div>

        <!-- Pagination for ways list -->
        <div
          v-if="allWaysBetweenSelectedItems.length > 50"
          class="ways-menu__wayspagination"
        >
          <button-item :disabled="!canNavigateDown" @click="showPreviousWays">
            <lucide-icon name="arrow-left" />
          </button-item>
          <div>
            {{ $t("labels.wayX", [waysStartIndex + 1]) }} -
            {{ wayEndIndex }}
          </div>
          <button-item :disabled="!canNavigateUp" @click="showNextWays">
            <lucide-icon name="arrow-right" />
          </button-item>
        </div>
      </div>
      <div v-else>
        {{ $t("messages.noWayExisting") }}
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

import InputNumber from "@core/input/InputNumber.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import RadioButton from "@core/input/RadioButton.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useWayStore } from "@editor/items/way/ways.store";
import { useResourceStore } from "@editor/items/resource/resources.store";

import type { Handleable } from "@core/types/Events";

const { t } = useI18n();

const waysStartIndex = ref(0);

const inspectorView = useInspectorView();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const wayStore = useWayStore();

const maxWayLength = computed({
  get: () => inspectorView.maxWayLength,
  set: (value: number) => inspectorView.set({ maxWayLength: value })
});

const wayStartItemId = computed({
  get: () => inspectorView.wayStartItemId,
  set: (value: number | null) => inspectorView.set({ wayStartItemId: value })
});

const wayStartItemType = computed({
  get: () => inspectorView.wayStartItemType,
  set: (value: "target") => inspectorView.set({ wayStartItemType: value })
});

const wayEndItemId = computed({
  get: () => inspectorView.wayEndItemId,
  set: (value: number | null) => inspectorView.set({ wayEndItemId: value })
});

const wayEndItemType = computed({
  get: () => inspectorView.wayEndItemType,
  set: (value: "target") => inspectorView.set({ wayEndItemType: value })
});

const wayStartItem = computed(() => {
  if (wayStartItemId.value == null) return null;
  if (wayStartItemType.value == "target") {
    return targetStore.items[wayStartItemId.value] || null;
  } else {
    return resourceStore.items[wayStartItemId.value] || null;
  }
});

const wayEndItem = computed(() => {
  if (wayEndItemId.value == null) return null;
  if (wayEndItemType.value == "target") {
    return targetStore.items[wayEndItemId.value] || null;
  } else {
    return resourceStore.items[wayEndItemId.value] || null;
  }
});

const targets = computed(() => targetStore.items);

const selectedWayIndex = computed({
  get: () => inspectorView.wayShown,
  set: (wayId: number) => inspectorView.set({ wayShown: wayId })
});

const startAndEndSet = computed(() => wayStartItem.value != null && wayEndItem.value != null);

const allWaysBetweenSelectedItems = computed(() => inspectorView.allWaysBetweenSelectedItems);

const wayEndIndex = computed(() => {
  return Math.min(waysStartIndex.value + 50, allWaysBetweenSelectedItems.value.length);
});

const limitedWaysBetweenItems = computed(() => {
  return allWaysBetweenSelectedItems.value.slice(waysStartIndex.value, wayEndIndex.value);
});

const canNavigateDown = computed(() => waysStartIndex.value > 0);
const canNavigateUp = computed(() => wayEndIndex.value < allWaysBetweenSelectedItems.value.length - 1);

watch(allWaysBetweenSelectedItems, (newItems) => {
  if (selectedWayIndex.value != null && selectedWayIndex.value > newItems.length) {
    selectedWayIndex.value = 0;
  }
});

function showNextWays() {
  waysStartIndex.value += 50;
}

function showPreviousWays() {
  waysStartIndex.value = Math.max(0, waysStartIndex.value - 50);
}

function setStartItem(item: { id: number; type: "target" } | null) {
  if (!item) {
    wayStartItemId.value = null;
  } else {
    wayStartItemId.value = item.id;
    wayStartItemType.value = item.type;
  }
}

function setEndItem(item: { id: number; type: "target" } | null) {
  if (!item) {
    wayEndItemId.value = null;
  } else {
    wayEndItemId.value = item.id;
    wayEndItemType.value = item.type;
  }
}

async function openAsWay(
  mouseEvent: MouseEvent & Handleable,
  wayData: {
    id: number;
    items: { id: number; type: "target" | "resource" }[];
    relationIds: number[];
  }
) {
  if (mouseEvent.hasBeenHandled) return;
  console.log("openAsWay", wayData);

  const way = await wayStore.create({
    title: t("labels.wayX", [wayData.id + 1]),
    startItemId: wayData.items[0].id,
    startItemType: wayData.items[0].type,
    relationIds: wayData.relationIds,
    complete: true
  });

  // move to way impulse tracking menu
  inspectorView.set({
    waysSubview: "impulseTracking"
  });

  wayStore.setActive(way.id);

  console.log("created way", way);
}
</script>

<style lang="scss">
.ways-menu {
  &__maxlengthinput {
    display: grid;
    grid-template-columns: 1fr 3rem;
  }
  &__waysheader {
    display: grid;
    grid-template-columns: 1fr 2.5rem;
  }
  &__waycontent {
    display: grid;
    grid-template-columns: 1fr 2.5em 2rem;
    gap: 0.5rem;
    align-items: center;
    width: 100%;
    padding-left: 0.5rem;
  }

  &__wayslist {
    padding: 0.5em 0;
    padding-right: 0.25em;
  }
  &__wayspagination {
    padding-top: 0.5em;
    text-align: center;
    display: flex;
    display: grid;
    grid-template-columns: 2.5rem 1fr 2.5rem;
    align-items: center;
  }
}
</style>
